import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import TableChartIcon from "@mui/icons-material/TableChart";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import PeopleIcon from "@mui/icons-material/People";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { nanoid } from "nanoid";
import { db } from "../firebaseConfig";
import { writeBatch, doc, collection } from "firebase/firestore";
import { devConsolelog } from "../Utility";

const AdminPanelIndex = () => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const adminPages = [
    {
      title: "Requests",
      description: "Manage all travel requests - view, add, update status",
      icon: <TableChartIcon sx={{ fontSize: 40 }} />,
      path: "/admin/requests",
      color: "#1976d2",
    },
    {
      title: "Bookings",
      description: "View and manage all bookings across the platform",
      icon: <BookOnlineIcon sx={{ fontSize: 40 }} />,
      path: "/admin/bookings",
      color: "#2e7d32",
    },
    {
      title: "Users",
      description: "Manage agents and suppliers accounts",
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      path: "/admin/users",
      color: "#ed6c02",
    },
  ];

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setMessage(null);
  };

  const parseCSV = (text) => {
    const rows = [];
    let currentRow = [];
    let currentField = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        currentRow.push(currentField.trim());
        currentField = "";
      } else if ((char === "\n" || char === "\r") && !inQuotes) {
        if (currentField !== "" || currentRow.length > 0) {
          currentRow.push(currentField.trim());
          rows.push(currentRow);
          currentRow = [];
          currentField = "";
        }
        if (char === "\r" && text[i + 1] === "\n") i++;
      } else {
        currentField += char;
      }
    }
    if (currentField !== "" || currentRow.length > 0) {
      currentRow.push(currentField.trim());
      rows.push(currentRow);
    }

    const headers = rows[0];
    return rows.slice(1).map((row) => {
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = row[index] || "";
      });
      return obj;
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage({ severity: "error", text: "Please select a CSV file first." });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const text = await selectedFile.text();
      const results = parseCSV(text);
      devConsolelog("upload pkg 1", results, text);
      if (results.length === 0) {
        setMessage({ severity: "error", text: "CSV file is empty." });
        setUploading(false);
        return;
      }

      const batch = writeBatch(db);
      let uploadCount = 0;

      for (const row of results) {
        const pkgId = `PKG-${row["Destination"].replace(" ", "")}-${nanoid(4)}`;
        const cabPricing = [];
        const optionKeys = Object.keys(row).filter((key) => key.startsWith("Option "));

        optionKeys.forEach((key) => {
          const val = row[key];
          if (val) {
            const parts = val.split("/-");
            if (parts.length > 2) {
              const price = parts[0].trim().replace(/[^0-9]/g, "");
              // const details = parts[1].trim().split(" ");
              const cabType = parts[1].trim();  //details[0];
              const details = parts[2].trim().split(" ");
              const seats = details[0];  //details.slice(1).join(" ");

              cabPricing.push({
                cabType,
                seats,
                price: parseInt(price) || 0,
              });
            }
          }
        });

        const packageData = {
          id: pkgId,
          type: row["Type"]?.toLowerCase() === "domestic" ? "domestic" : "international",
          destination: row["Destination"],
          duration: row["Package Duration"],
          title: row["Package Name"],
          route: row["Route"],
          pickup: row["Pickup"],
          drop: row["Drop"],
          itinerary: row["Itinerary"],
          detailedItinerary: row["Detail Itinerary"],
          currency: row["Currency"] || "INR",
          cabPricing: cabPricing,
          priceType: row["Price Type"],
          taxes: row["Taxes"],
          priceInclusion: row["Price inclusion Type"],
          additionalPrice: row["Additional Price"],
          policies: row["Policies"],
          createdAt: Date.now(),
        };

        devConsolelog("upload pkg ", pkgId, packageData, uploadCount)
        const pkgRef = doc(collection(db, "transportPackages"), pkgId);
        batch.set(pkgRef, packageData);
        uploadCount++;
      }

      await batch.commit();
      setMessage({
        severity: "success",
        text: `Successfully uploaded ${uploadCount} packages.`,
      });
      setSelectedFile(null);
    } catch (error) {
      console.error("upload packages error:", error);
      setMessage({
        severity: "error",
        text: error.message || "Failed to upload packages. Please check the file format.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Admin Panel
      </Typography>

      {/* Package Upload Section */}
      <Card sx={{ mb: 4, p: 2, borderLeft: "5px solid #fbc02d" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <UploadFileIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Bulk Upload Transport Packages
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload a CSV file containing package details, pricing, and itineraries to update the catalog.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", sm: "row", gap: 2, alignItems: "center" }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
            >
              Select CSV File
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileChange}
              />
            </Button>

            {selectedFile && (
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {uploading ? "Uploading..." : "Upload Packages"}
            </Button>
          </Box>

          {message && (
            <Alert severity={message.severity} sx={{ mt: 2 }}>
              {message.text}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {adminPages.map((page) => (
          <Grid item xs={12} sm={4} key={page.title}>
            <Card>
              <CardActionArea onClick={() => navigate(page.path)}>
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    py: 4,
                  }}
                >
                  <Box sx={{ color: page.color, mb: 2 }}>{page.icon}</Box>
                  <Typography variant="h6" fontWeight={600}>
                    {page.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {page.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AdminPanelIndex;